/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-27 02:05:19
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Layout, Tabs } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import MainMenu from 'src/components/containers/main-menu';
import { getShop } from 'src/helpers';

import { URL } from 'src/constants/links';
import DidYouKnow from 'src/components/views/tools/integration/did-you-know';
import DefaultCard from 'src/components/views/tools/integration/tabs/default-card';
import SwitchAppIntegrationModal from 'src/components/views/tools/integration/switch-app-integration-modal';
import RemoveAppIntegrationModal from 'src/components/views/tools/integration/remove-app-integration-modal';
import RecommendedAppsBanners from 'src/components/views/tools/integration/recommended-apps-banners';
import SendRequestFeatureModal from 'src/components/containers/common/send-request-feature-modal';
import RequestResultModal from 'src/components/containers/common/request-result-modal';
import {
  MULTI_LANGUAGE,
  MULTI_CURRENCY,
  WISHLIST,
  PRODUCT_LABELS,
  // PRODUCT_SWATCHES,
  PRODUCT_REVIEWS,
  PAGE_BUILDERS,
  OTHERS,
  initIntegrationStructure
} from 'src/constants/integration';
import * as AppIntegrationActions from 'actions/app-integration-actions';
import * as OauthAction from 'actions/oauth-actions';
import * as integrationAction from 'actions/integration-actions';
import * as suportTicketActions from 'src/stores/actions/support-ticket-actions';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';
import ThemeUpgradeFlow2Notification from 'src/components/containers/common/theme-upgrade-flow-2-notification';
import { FEATURE_IDS } from 'src/constants/theme-upgrade';
import TitleHelp from 'src/components/views/common/title-help';

type Props = {
  getIntegrationBanners: () => Array<any>,
  handleChangeSwitchApp: (appType: string, appName: string, appKey: string) => void,
  handleContinueSwitchApp: (appType: string, appName: string, appKey: string) => void,
  handleChangeRemoveApp: (appType: string, appName: string, appKey: string) => void,
  handleContinueRemoveApp: (appType: string, appName: string, appKey: string) => void,
  updateCurrentAppIntegration: (appType: string, appName: string, appKey: string) => void,
  switchAppModalActive: boolean,
  switchAppName: string,
  switchAppKey: string,
  switchAppLoading: boolean,
  removeAppModalActive: boolean,
  removeAppName: string,
  removeAppLoading: boolean,
  emailSupport: string
};

type ComposedProps = Props & WithI18nProps;

type State = {
  selected: number,
  dismissedBanner: boolean
};

class Integration extends Component<ComposedProps, State> {
  state = {
    selected: 0,
    dismissedBanner: false,

    // Modal send request
    activeRequestModal: false,
    activeModalResult: false
  };

  async componentDidMount() {
    // Hide banner
    this.checkDismissedBanner();
    this.props.getIntegrationBanners();
  }

  componentWillReceiveProps(nextProps) {
    const { switchAppLoading, removeAppLoading } = this.props;
    const { switchAppLoading: nextSwitchAppLoading, removeAppLoading: nextRemoveAppLoading } =
      nextProps;

    if (switchAppLoading && !nextSwitchAppLoading) {
      this.updateCurrentAppIntegration();
    }

    if (removeAppLoading && !nextRemoveAppLoading) {
      this.removeCurrentAppIntegration();
    }
  }

  render() {
    const { selected, activeRequestModal, activeModalResult } = this.state;
    const {
      i18n,
      banners,
      emailSupport,
      switchAppModalActive,
      switchAppLoading,
      removeAppModalActive,
      removeAppLoading
    } = this.props;

    // Product reviews -> Wishlist -> Multiple Language -> Product Labels -> Multiple Currency -> Product Swatches -> Page Builder
    const tabs = [
      {
        id: 'product-reviews',
        content: i18n.translate('AdminHTML.Integration.Integration.productReviewsTab'),
        panelID: 'product-reviews-content'
      },
      {
        id: 'wishlist',
        content: i18n.translate('AdminHTML.Integration.Integration.wishlistTab'),
        panelID: 'wishlist-content'
      },
      {
        id: 'multiple-language',
        content: i18n.translate('AdminHTML.Integration.Integration.multipleLanguageTab'),
        panelID: 'multiple-language-content'
      },
      {
        id: 'product-labels',
        content: i18n.translate('AdminHTML.Integration.Integration.productLabelsTab'),
        panelID: 'product-labels-content'
      },
      {
        id: 'multiple-currency',
        content: i18n.translate('AdminHTML.Integration.Integration.multipleCurrencyTab'),
        panelID: 'multiple-currency-content'
      },
      // {
      //   id: 'product-swatches',
      //   content: i18n.translate('AdminHTML.Integration.Integration.productSwatchesTab'),
      //   panelID: 'product-swatches-content'
      // },
      {
        id: 'page-builder',
        content: i18n.translate('AdminHTML.Integration.Integration.pageBuilderTab'),
        panelID: 'page-builder'
      },
      {
        id: 'others',
        content: i18n.translate('AdminHTML.Integration.Integration.othersTab'),
        panelID: 'others-content'
      }
    ];

    const tabComponents = [
      <DefaultCard list={initIntegrationStructure(PRODUCT_REVIEWS)} emailSupport={emailSupport} />,
      <DefaultCard list={initIntegrationStructure(WISHLIST)} emailSupport={emailSupport} />,
      <DefaultCard list={initIntegrationStructure(MULTI_LANGUAGE)} emailSupport={emailSupport} />,
      <DefaultCard list={initIntegrationStructure(PRODUCT_LABELS)} emailSupport={emailSupport} />,
      <DefaultCard list={initIntegrationStructure(MULTI_CURRENCY)} emailSupport={emailSupport} />,
      // <DefaultCard list={initIntegrationStructure(PRODUCT_SWATCHES} emailSupport={emailSupport} />,
      <DefaultCard list={initIntegrationStructure(PAGE_BUILDERS)} emailSupport={emailSupport} />,
      <DefaultCard list={initIntegrationStructure(OTHERS)} emailSupport={emailSupport} />
    ];

    const { dismissedBanner } = this.state;

    return (
      <div className="BC-Page BC-Page__Integration">
        <MainMenu menuId="menu-integration-third-party" />
        <Page title={i18n.translate('AdminHTML.Integration.Integration.title')}>
          <TitleHelp i18n={i18n} hrefUserManual={URL['Link_V2_Tool_Integration_ManualGuide']} />
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Installation reminder status banner */}
                <InstallationRemindStatusBanner />

                <ThemeUpgradeFlow2Notification featureID={FEATURE_IDS.INTEGRATION} />

                {/* Did you know? */}
                {!dismissedBanner ? (
                  <DidYouKnow
                    onDismiss={this.handleDismissBanner}
                    sendRequest={() => this.toggleModal('activeRequestModal')}
                  />
                ) : (
                  ''
                )}

                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />
              </div>
            </Layout.Section>

            {/* Banner */}
            <RecommendedAppsBanners banners={banners} />

            {/* Tabs */}
            <Layout.Section>
              <Tabs tabs={tabs} selected={selected} onSelect={this.handleTabChange}>
                <div className="tabContent">{tabComponents[selected]}</div>
              </Tabs>
            </Layout.Section>
          </Layout>

          <SwitchAppIntegrationModal
            active={switchAppModalActive}
            loading={switchAppLoading}
            handleChange={this.props.handleChangeSwitchApp}
            handleContinue={this.handleContinueSwitchApp}
          />

          <RemoveAppIntegrationModal
            active={removeAppModalActive}
            loading={removeAppLoading}
            handleChange={this.props.handleChangeRemoveApp}
            handleContinue={this.handleContinueRemoveApp}
          />

          <SendRequestFeatureModal
            active={activeRequestModal}
            handleClose={() => this.toggleModal('activeRequestModal')}
            handleOpenResultModal={() => this.toggleModal('activeModalResult')}
            featureKey="integrationApp"
          />

          <RequestResultModal
            active={activeModalResult}
            handleClose={() => this.toggleModal('activeModalResult')}
            featureName={i18n.translate(
              'AdminHTML.Common.RequestResultModal.content_t1_integrationApp'
            )}
          />
        </Page>
      </div>
    );
  }

  // Tab change event handler
  handleTabChange = selectedTabIndex => {
    this.setState({ selected: selectedTabIndex });
  };

  // Get dismissed banner status
  checkDismissedBanner = () => {
    const shop = getShop();
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(`${shop}-dismissBannerInIntegrationPage`);
    } catch (error) {}

    if (dismissed === 'true') {
      this.setState({ dismissedBanner: true });
    }
  };

  // Handle hide banner
  handleDismissBanner = () => {
    const shop = getShop();
    try {
      localStorage.setItem(`${shop}-dismissBannerInIntegrationPage`, true);
    } catch (error) {}

    // Hide banner
    this.setState({ dismissedBanner: true });
  };

  handleContinueSwitchApp = () => {
    const { switchAppType, switchAppName, switchAppKey } = this.props;

    if (!switchAppName) return;

    this.props.handleContinueSwitchApp(switchAppType, switchAppName, switchAppKey);
  };

  handleContinueRemoveApp = () => {
    const { removeAppType, removeAppName } = this.props;
    if (!removeAppName) return;

    this.props.handleContinueRemoveApp(removeAppType, removeAppName);
  };

  updateCurrentAppIntegration = () => {
    const { switchAppType, switchAppName, switchAppKey } = this.props;
    this.props.updateCurrentAppIntegration(switchAppType, switchAppName, switchAppKey);
  };

  removeCurrentAppIntegration = () => {
    const { removeAppType } = this.props;

    this.props.updateCurrentAppIntegration(removeAppType, '', '');
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getIntegrationBanners: () => dispatch(integrationAction.getIntegrationBanners()),
  handleChangeSwitchApp: (appType, appName, appKey) =>
    dispatch(AppIntegrationActions.handleChangeSwitchApp(appType, appName, appKey)),
  handleContinueSwitchApp: (appType, appName, appKey) =>
    dispatch(
      AppIntegrationActions.handleContinueSwitchApp(appType, appName, appKey, ownProps.i18n)
    ),
  handleChangeRemoveApp: (appType, appName, appKey) =>
    dispatch(AppIntegrationActions.handleChangeRemoveApp(appType, appName, appKey)),
  handleContinueRemoveApp: (appType, appName) =>
    dispatch(AppIntegrationActions.handleContinueRemoveApp(appType, appName, ownProps.i18n)),
  updateCurrentAppIntegration: (appType, appName, appKey) =>
    dispatch(OauthAction.updateCurrentAppIntegration(appType, appName, appKey)),
  sendSupportTicket: data => dispatch(suportTicketActions.sendSupportTicket(data, ownProps.i18n)),
  resetSendSupportTicket: () => dispatch(suportTicketActions.resetSendSupportTicket())
});

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const externalInfo = _.get(tenantApp, 'externalInfo', {});

  return {
    switchAppModalActive: _.get(state.appIntegration, 'switchAppModalActive', false),
    switchAppType: _.get(state.appIntegration, 'switchAppType', ''),
    switchAppName: _.get(state.appIntegration, 'switchAppName', ''),
    switchAppKey: _.get(state.appIntegration, 'switchAppKey', ''),
    switchAppLoading: _.get(state.appIntegration, 'switchAppLoading', false),

    removeAppModalActive: _.get(state.appIntegration, 'removeAppModalActive', false),
    removeAppType: _.get(state.appIntegration, 'removeAppType', ''),
    removeAppName: _.get(state.appIntegration, 'removeAppName', ''),
    removeAppLoading: _.get(state.appIntegration, 'removeAppLoading', false),

    emailSupport: _.get(state.oauth, 'emailSupport'),
    banners: _.get(state.integration, 'banners'),
    email: _.get(externalInfo, 'email', '')
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(Integration));
